import { z } from "zod";
import { DAYS_OF_WEEK } from "./activity-availabilities";
import { localizedTextSchema, requiredLocalizedTextSchema, timestampsSchema } from "./common";
import { listQueryBaseSchema, queryBoolean } from "./list-query";
import { mediaDtoSchema } from "./media";
import { ACTIVITY_STATUS } from "../constants/status";
import { activityTagsSchema } from "./venue-tags";
import { openingHoursSchema } from "./opening-hours";
import { tablePolicySchema } from "./table-policy";

const statusValues = Object.values(ACTIVITY_STATUS) as [number, ...number[]];

/**
 * Every value `activities.segment` can hold, in one place.
 *
 * It used to be spelled out separately in the DTO schema and the upsert schema.
 * When `nightlife` and `beach-clubs` were added to the Postgres enum, both
 * copies were missed, so re-tagged rows read back fine but any admin save on
 * one failed zod validation. One const, imported by both, removes that gap.
 * Keep it in step with `activitySegmentEnum` in packages/db.
 */
export const ACTIVITY_SEGMENTS = [
  "activities",
  "bookable",
  "restaurants",
  "stays",
  "spas",
  "locations",
  "guides",
  "golf",
  "nightlife",
  "beach-clubs",
] as const;
export const activitySegmentSchema = z.enum(ACTIVITY_SEGMENTS);
export type ActivitySegment = (typeof ACTIVITY_SEGMENTS)[number];

/**
 * How an activity's `commission` value is read:
 *   - `fixed`   → MAD per guest; a reservation defaults to
 *                 `commission × (adults + children + infants)`.
 *   - `percent` → percent of the reservation price; a reservation defaults to
 *                 `round(reservationPrice × commission / 100)`.
 */
export const COMMISSION_TYPES = ["fixed", "percent"] as const;
export const commissionTypeSchema = z.enum(COMMISSION_TYPES);
export type CommissionType = (typeof COMMISSION_TYPES)[number];

/**
 * Commission a reservation should default to, given the activity's rate, its
 * commission type, the head count and the reservation price. Single source of
 * truth for the API auto-fill, the admin create form and the quick-add dialog.
 */
export function resolveReservationCommission(input: {
  rate: number | null | undefined;
  /** The booked package's own rate, read with the activity's type. Wins over
   * `rate` when set, so one page can carry options with different cuts. */
  packageRate?: number | null;
  type: CommissionType | null | undefined;
  guests: number;
  reservationPrice: number;
  /** Vehicle count for per-vehicle (typePrice 3) packages. When set, a fixed
   * rate reads per vehicle — full rate for a solo-ridden vehicle, half rate
   * for a shared one — instead of per guest. */
  vehicles?: number | null;
}): number {
  const rate = input.packageRate ?? input.rate ?? 0;
  if (rate <= 0) return 0;
  if (input.type === "percent") {
    return Math.round((input.reservationPrice * rate) / 100);
  }
  const vehicles = input.vehicles ?? 0;
  if (vehicles > 0) {
    // Riders spread one-per-vehicle before doubling up, so vehicles with a
    // single rider = 2×vehicles − guests (clamped to [0, vehicles]).
    const solo = Math.min(vehicles, Math.max(0, 2 * vehicles - input.guests));
    const shared = vehicles - solo;
    return rate * solo + (rate / 2) * shared;
  }
  return rate * Math.max(1, input.guests);
}

const activityStatusSchema = z.union([
  z.literal(statusValues[0]),
  ...statusValues.slice(1).map((v) => z.literal(v)),
]);

export const activityDtoSchema = z
  .object({
    id: z.string().uuid(),
    title: localizedTextSchema,
    subtitle: localizedTextSchema,
    overview: localizedTextSchema,
    menuLink: localizedTextSchema,
    slug: z.string(),
    /** Nullable for explore-mode rows (attractions, landmarks). */
    categoryId: z.string().uuid().nullable(),
    category: z
      .object({ id: z.string().uuid(), slug: z.string().nullable().optional(), name: localizedTextSchema })
      .nullable(),
    cityId: z.string().uuid().nullable(),
    city: z
      .object({
        id: z.string().uuid(),
        name: localizedTextSchema,
        slug: z.string().nullable().default(null),
      })
      .nullable(),
    latitude: z.string().nullable(),
    longitude: z.string().nullable(),
    /** Street address printed in booking emails; null when unknown. */
    address: z.string().nullable().default(null),
    hideLocation: z.boolean(),
    status: activityStatusSchema,
    featured: z.boolean(),
    isPromoted: z.boolean(),
    isTrending: z.boolean(),
    acceptOnlinePayment: z.boolean(),
    acceptCashPayment: z.boolean(),
    /** Provider takes cash only on arrival (no card machine). */
    cashOnlyOnArrival: z.boolean().default(false),
    reserveNowPayLater: z.boolean(),
    freeCancellation: z.boolean(),
    confirmationViaWhatsapp: z.boolean(),
    activityPeriod: z.string().nullable(),
    discount: z.number().int().nullable(),
    /** Per-activity commission — MAD per guest, or a percentage of the
     *  reservation price when `commissionType` is `"percent"`. */
    commission: z.number().int().nonnegative().default(0),
    commissionType: commissionTypeSchema.default("fixed"),
    /** Off sale without being unpublished — see isBookingPaused. */
    bookingPaused: z.boolean().default(false),
    bookingResumesOn: z.string().nullable().default(null),
    bookingPausedNote: z.string().nullable().default(null),
    /** Women-only group trip (organiser, seats per date, deposit, fixed dates). */
    womenOnly: z.boolean().default(false),
    organiserId: z.string().uuid().nullable().default(null),
    seatLimit: z.number().int().nullable().default(null),
    depositAmount: z.number().int().nullable().default(null),
    tripDates: z.array(z.string()).default([]),
    tripDepartures: z.array(z.string()).default([]),
    minPeople: z.number().int(),
    infantMaxAge: z.number().int().nullable(),
    childrenMaxAge: z.number().int().nullable(),
    minNight: z.number().int(),
    openDays: z.array(z.string()).nullable().optional(),
    minNoticeHours: z.number().int().nullable().optional(),
    /** Weekly door hours (display only, not the booking calendar). Null = unknown. */
    openingHours: openingHoursSchema.optional(),
    /** A restaurant's house rules (`tablePolicySchema`). Read-only here. */
    tablePolicy: tablePolicySchema.nullish(),
    rating: z.string(),
    viewsCount: z.number().int(),
    relatedActivityIds: z.array(z.string().uuid()),
    heroMedia: mediaDtoSchema.nullable(),
    galleryMedia: z.array(mediaDtoSchema),
    reservationsCount: z.number().int().nonnegative().optional(),
    revenue: z.number().nonnegative().optional(),
    /** Live packages on the activity (admin list only). */
    packagesCount: z.number().int().nonnegative().optional(),
    /** Lowest positive adult package price in MAD, null when none (admin list only). */
    fromPrice: z.number().nonnegative().nullable().optional(),
    /**
     * Per-template content blocks. Shape depends on the category template
     * (validated server-side via `pickBlocksSchema`). `{}` for categories
     * without a template.
     */
    contentBlocks: z.record(z.string(), z.unknown()).default({}),
    /** Mirrors `category.template` for client-side dispatch. */
    template: z.string().nullable().optional(),
    /** 'bookable' | 'explore'. Used by Discover + admin to split surfaces. */
    mode: z.enum(["bookable", "explore"]).default("bookable"),
    /** High-level segment for Discover grouping. */
    segment: activitySegmentSchema.default("activities"),
    /** Long-form rich body (markdown) for explore-mode editorial content. */
    bodyMarkdown: z.string().nullable().default(null),
    /** Hero image URL for explore-mode rows. */
    heroImageUrl: z.string().nullable().default(null),
    /** Gallery image URLs for explore-mode rows. */
    galleryImageUrls: z.array(z.string()).default([]),
    /** Display-only social-proof count. */
    mentions: z.number().int().default(0),
    /** Editorial facts (bullet points). */
    facts: z.array(z.string()).default([]),
    /** FAQs. */
    faqs: z
      .array(z.object({ question: z.string(), answer: z.string() }))
      .default([]),
    /** Venue sub-type tags for Discover sub-filters (rooftop, bar, …). Canonical keys, not localized. */
    tags: activityTagsSchema,
    /** Overview sections (parsed from MDX body). */
    overviewSections: z
      .array(z.object({ title: z.string(), body: z.string() }))
      .default([]),
  })
  .extend(timestampsSchema.shape);
export type ActivityDTO = z.infer<typeof activityDtoSchema>;

export const activityUpsertSchema = z.object({
  title: requiredLocalizedTextSchema,
  subtitle: localizedTextSchema,
  overview: localizedTextSchema,
  menuLink: localizedTextSchema,
  slug: z.string().optional(),
  /**
   * Nullable so explore-mode rows (attractions, landmarks) can omit a category.
   * The service enforces categoryId != null when mode='bookable'.
   */
  categoryId: z.string().uuid().nullable(),
  cityId: z.string().uuid().nullable(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  /** Empty or omitted clears it: no address is better than a guessed one. */
  address: z.string().max(512).nullable().optional(),
  hideLocation: z.boolean().default(false),
  status: activityStatusSchema,
  featured: z.boolean().default(false),
  isPromoted: z.boolean().default(false),
  isTrending: z.boolean().default(false),
  acceptOnlinePayment: z.boolean().default(false),
  acceptCashPayment: z.boolean().default(false),
  cashOnlyOnArrival: z.boolean().default(false),
  reserveNowPayLater: z.boolean().default(true),
  freeCancellation: z.boolean().default(true),
  confirmationViaWhatsapp: z.boolean().default(true),
  activityPeriod: z.string().optional(),
  discount: z.number().int().min(0).max(100).optional(),
  /** Per-activity commission — MAD per guest, or a percentage of the
   *  reservation price when `commissionType` is `"percent"`. */
  commission: z.number().int().nonnegative().optional(),
  commissionType: commissionTypeSchema.optional(),
  bookingPaused: z.boolean().optional(),
  bookingResumesOn: z.string().nullable().optional(),
  bookingPausedNote: z.string().nullable().optional(),
  /** Women-only trip fields; omitted leaves the stored values alone. */
  womenOnly: z.boolean().optional(),
  organiserId: z.string().uuid().nullable().optional(),
  seatLimit: z.number().int().min(1).max(500).nullable().optional(),
  depositAmount: z.number().int().min(0).max(100000).nullable().optional(),
  tripDates: z
    .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
    .max(200)
    .optional(),
  tripDepartures: z.array(z.string().trim().min(1).max(120)).max(10).optional(),
  minPeople: z.number().int().min(1).default(1),
  infantMaxAge: z.number().int().optional(),
  childrenMaxAge: z.number().int().optional(),
  minNight: z.number().int().default(0),
  /** Weekdays bookings are taken on. Null or empty means every day. */
  openDays: z.array(z.enum(DAYS_OF_WEEK)).nullable().optional(),
  /** Hours of notice the provider needs (48 = two days ahead). Null or 0: none. */
  minNoticeHours: z.number().int().min(0).max(720).nullable().optional(),
  /**
   * Weekly door hours. Omitted keeps the stored value (so older admin builds
   * that never send it cannot wipe it); null clears it.
   */
  openingHours: openingHoursSchema.optional(),
  relatedActivityIds: z.array(z.string().uuid()).default([]),
  /**
   * Per-template content blocks. Validated by the API layer against the
   * schema picked from the category's `template`. Pass `{}` for categories
   * with no template; the API will normalize.
   */
  contentBlocks: z.record(z.string(), z.unknown()).optional(),
  /** Explore-mode fields. Optional on upsert; the service preserves existing values when omitted. */
  mode: z.enum(["bookable", "explore"]).optional(),
  segment: activitySegmentSchema.optional(),
  bodyMarkdown: z.string().nullable().optional(),
  heroImageUrl: z.string().nullable().optional(),
  galleryImageUrls: z.array(z.string()).optional(),
  mentions: z.number().int().nonnegative().optional(),
  facts: z.array(z.string()).optional(),
  faqs: z.array(z.object({ question: z.string(), answer: z.string() })).optional(),
  overviewSections: z.array(z.object({ title: z.string(), body: z.string() })).optional(),
  tags: activityTagsSchema.optional(),
});
export type ActivityUpsertInput = z.infer<typeof activityUpsertSchema>;

export const activitiesListQuerySchema = listQueryBaseSchema.extend({
  categoryId: z.string().uuid().optional(),
  cityId: z.string().uuid().optional(),
  status: z.coerce.number().optional(),
  featured: queryBoolean(),
  isPromoted: queryBoolean(),
  isTrending: queryBoolean(),
  acceptOnlinePayment: queryBoolean(),
  freeCancellation: queryBoolean(),
  createdFrom: z.iso.date().optional(),
  createdTo: z.iso.date().optional(),
  /**
   * Activity mode filter. Defaults to 'bookable' on the admin list so
   * Discover's explore-only rows (attractions, landmarks) don't pollute
   * the Activities page. Pass 'explore' for the Explore Listings page,
   * or 'all' to bypass the filter.
   */
  mode: z.enum(["bookable", "explore", "all"]).optional(),
});
export type ActivitiesListQuery = z.infer<typeof activitiesListQuerySchema>;
